import { useEffect, useState } from "react";
import { Routes, Route } from "react-router-dom";

import { validateToken } from "@/utils/helper.js";

import { useUserContext } from "@/context/UserContext.jsx";

// Import pages
import IndexPage from "../pages/index.jsx";
import Login from "../pages/login.jsx";
import Home from "../pages/home.jsx";
import AccountDetails from "../pages/account_details.jsx";
import AccountsDistribution from "../pages/accounts_distribution.jsx";

// Lead Inventory
import LeadInventoryYours from "../pages/lead_inventory/yours.jsx";
import LeadInventoryYoursPage from "../pages/lead_inventory/yours_page.jsx";
import LeadInventoryAllPage from "../pages/lead_inventory/all_page.jsx";
import LeadInventorySharkTankPage from "../pages/lead_inventory/shark_tank_page.jsx";

// Account Inventory
import AccountInventoryYours from "../pages/account_inventory/yours.jsx";
import AccountInventoryYoursPage from "../pages/account_inventory/acc_yours_page.jsx";
import AccountInventoryAllPage from "../pages/account_inventory/acc_all_page.jsx";

// Account Distribution
import AccountDistributionIncoming from "../pages/account_distribution/incoming.jsx";
import AccountDistributionIncomingPage from "../pages/account_distribution/acc_incoming_page.jsx";
import AccountDistributionAssignedPage from "../pages/account_distribution/acc_assigned_page.jsx";

// Opportunity Inventory
import OpportunityInventoryYours from "../pages/opportunity_inventory/yours.jsx";
import OpportunityInventoryYoursPage from "../pages/opportunity_inventory/yours_page.jsx";
import OpportunityInventoryAllPage from "../pages/opportunity_inventory/all_page.jsx";

// Journey pages
import LeadJourney from "../pages/lead_journey.jsx";
import OpportunityJourney from "../pages/opportunity_journey.jsx";
import OpportunityReferralPipeline from "../pages/opportunity_referral_pipeline.jsx";

// Other pages
import ComponentConstructionSite from "../pages/component_construction_site.jsx";
import Referral from "../pages/referral/referral.jsx";
import AccDetail from "../pages/acc/[acc_id].jsx";
import Upload from "../pages/upload.jsx";
import PageHeader from "@/components/pageHeader.jsx";
import NotFound from "@/pages/NotFound.js";
import Admin from "@/pages/Admin.js";
import LeadDistribution from "@/pages/LeadDistribution.js";
import LoginCallback from "@/pages/loginCallback.jsx";
import Loader from "@/components/loader/index.js";
import axios from "axios";
import Settings from "../pages/settings.tsx";
import { fetchAndStoreWhoAmI } from "@/api/auth/whoami";

const Router = () => {
  const [isTokenValid, setIsTokenValid] = useState<boolean | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);

  const { token, setToken } = useUserContext();
  const refreshToken = localStorage.getItem("refresh_token");

  useEffect(() => {
    const checkToken = async () => {
      const role = localStorage.getItem("role");
      setIsAdmin(role === "Admin");

      if (token) {
        if (!validateToken(token)) {
          try {
            const response = await axios.post(
              `${import.meta.env.VITE_BASE_URL}/token/refresh`,
              {},
              {
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${refreshToken}`,
                },
              }
            );
            if (response?.data.token) {
              localStorage.setItem("token", response.data.token);
              setToken(response.data.token);
              setIsTokenValid(true);
            } else {
              localStorage.clear();
              setIsTokenValid(false);
            }
          } catch (err) {
            console.error("Token refresh failed:", err);
            localStorage.clear();
            setIsTokenValid(false);
          }
        } else {
          setIsTokenValid(true);
        }
      } else {
        setIsTokenValid(false);
      }

      if (token) {
        try {
          await fetchAndStoreWhoAmI();
          setIsAdmin(localStorage.getItem("role") === "Admin");
        } catch (whoamiErr) {
          console.warn("Failed to fetch /whoami:", whoamiErr);
        }
      }
    };

    checkToken();
  }, [token]);

  if (isTokenValid === null) return <Loader />;

  if (!isTokenValid) {
    return (
      <>
        <Routes>
          <Route path="/" element={<IndexPage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/auth/callback" element={<LoginCallback />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </>
    );
  } else {
    return (
      <>
        <PageHeader />
        <Routes>
          <Route path="/" element={<IndexPage />} />
          <Route path="/home" element={<Home />} />

          {/* Lead Distribution Routes */}
          <Route path="/leadDistribution" element={<LeadDistribution />} />

          {/* Lead Inventory Routes */}
          <Route path="/lead_inventory" element={<LeadInventoryYours />}>
            <Route path="yours" element={<LeadInventoryYoursPage />} />
            <Route path="all" element={<LeadInventoryAllPage />} />
            <Route path="shark_tank" element={<LeadInventorySharkTankPage />} />
          </Route>

          {/* Account Distribution Routes */}
          <Route
            path="/account_distribution"
            element={<AccountDistributionIncoming />}
          >
            <Route
              path="incoming"
              element={<AccountDistributionIncomingPage />}
            />
            <Route
              path="assigned"
              element={<AccountDistributionAssignedPage />}
            />
          </Route>

          {/* Account Inventory Routes */}
          <Route path="/account_inventory" element={<AccountInventoryYours />}>
            <Route path="yours" element={<AccountInventoryYoursPage />} />
            <Route path="all" element={<AccountInventoryAllPage />} />
          </Route>

          {/* Opportunity Inventory Routes */}
          <Route
            path="/opportunity_inventory"
            element={<OpportunityInventoryYours />}
          >
            <Route path="yours" element={<OpportunityInventoryYoursPage />} />
            <Route path="all" element={<OpportunityInventoryAllPage />} />
          </Route>

          {/* Journey Routes */}
          <Route path="/lead_journey" element={<LeadJourney />} />
          <Route path="/opportunity_journey" element={<OpportunityJourney />} />
          <Route
            path="/opportunity_referral_pipeline"
            element={<OpportunityReferralPipeline />}
          />

          {/* Other Routes */}
          <Route path="/account_details" element={<AccountDetails />} />
          <Route
            path="/accounts_distribution"
            element={<AccountsDistribution />}
          />
          <Route
            path="/component_construction_site"
            element={<ComponentConstructionSite />}
          />
          <Route path="/referral/referral" element={<Referral />} />
          <Route path="/acc/:acc_id" element={<AccDetail />} />
          <Route path="/user/:user_id" element={<AccDetail />} />
          <Route path="/user/:user_id/post/:post_id" element={<AccDetail />} />
          <Route path="/upload" element={<Upload />} />

          {/* Admin Route */}
          {isAdmin && <Route path="/admin" element={<Admin />} />}

          {/* Settings Route */}
          <Route path="/settings" element={<Settings />} />

          {/* Not Found Route */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </>
    );
  }
};

export default Router;
